import { useState, useEffect, useRef, Key } from 'react';
import { CircularProgress, Pagination, Skeleton, Box } from '@mui/material';
import Sidebar from './elements/Sidebar';
import { useUser } from '../contexts/UserContext';
import useEventFetching from '../hooks/events/useEventFetching';
import useEventGroups from '../hooks/events/useEventGroups';
import SelectSidebar from './elements/SelectSidebar';
import { getGroupId, getEventsEmptyMessage } from '../utils';
import { ALL_EVENTS_GROUP } from '../constants';
import { CARD_GRID_GAP, EVENT_CARD_WIDTH, EVENTS_PAGE_MAX_WIDTH, EVENTS_SIDEBAR_WIDTH } from '../constants/layout';
import SearchBar from './elements/SearchBar';
import CartDrawer from './cart/CartDrawer';
import EventsCard from './events/Eventscard';
import { useNavigate, useLocation } from 'react-router-dom';
import { Child } from '../types';
import { storeReturnToForEvents } from '../utils/navigationReturnTo';
import { useDebounce } from '../hooks/useDebounce';

const PAGE_PARAM = 'page';
const SEARCH_PARAM = 'search';

const getPageFromSearch = (search: string): number => {
    const params = new URLSearchParams(search);
    const p = params.get(PAGE_PARAM);
    if (!p) return 1;
    const n = parseInt(p, 10);
    return !isNaN(n) && n >= 1 ? n : 1;
};

const getInitialSelectedEventGroup = () => {
    // Prefer group_id from URL (e.g. when returning from Event Details),
    // otherwise fall back to stored group id. Use a lightweight placeholder
    // with just the id; once eventGroups load we replace it with the full group.
    try {
        const params = new URLSearchParams(window.location.search);
        const groupIdFromUrl = params.get('group_id');
        if (groupIdFromUrl) {
            return { id: String(groupIdFromUrl) };
        }
    } catch {
        // ignore, fall back to helper
    }
    const storedGroupId = getGroupId();
    if (storedGroupId) {
        return { id: String(storedGroupId) };
    }
    return ALL_EVENTS_GROUP;
};

const MainLayout = () => {
    const { user, selectedChild, setSelectedChild } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const [filteredEventGroups, setFilteredEventGroups] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(() => getPageFromSearch(location.search));
    const [selectedEventGroup, setSelectedEventGroup] = useState<any>(getInitialSelectedEventGroup);
    const { eventGroups, loading: eventGroupsLoading } = useEventGroups();
    const [searchQuery, setSearchQuery] = useState<string | null>(null);
    const debouncedSearchQuery = useDebounce(searchQuery, 500);
    const initializedRef = useRef(false);
    const hasUserTypedSearchRef = useRef(false);
    const groupsLoaded = !eventGroupsLoading;

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Set the initial event group and child when the page is loaded
    useEffect(() => {
        setFilteredEventGroups(eventGroups);

        if (eventGroupsLoading) {
            return;
        }

        const currentId =
            selectedEventGroup?.id != null ? String(selectedEventGroup.id) : null;

        if (currentId && currentId !== ALL_EVENTS_GROUP.id) {
            const fullGroup = eventGroups.find(
                (group) => String(group.id) === currentId,
            );
            if (fullGroup) {
                if (!initializedRef.current) {
                    setSelectedEventGroup({ ...fullGroup, id: String(fullGroup.id) });
                    initializedRef.current = true;
                }
                return;
            }

            setSelectedEventGroup(ALL_EVENTS_GROUP);
            const searchParams = new URLSearchParams(location.search);
            if (searchParams.has('group_id')) {
                searchParams.delete('group_id');
                const newSearch = searchParams.toString();
                navigate(newSearch ? `/events?${newSearch}` : '/events', {
                    replace: true,
                });
            }
            initializedRef.current = true;
            return;
        }

        if (eventGroups.length > 0 && !initializedRef.current) {
            if (!currentId) {
                const storedGroupId = getGroupId();
                if (storedGroupId) {
                    const fullGroup = eventGroups.find(
                        (group) => String(group.id) === String(storedGroupId),
                    );
                    if (fullGroup) {
                        setSelectedEventGroup({
                            ...fullGroup,
                            id: String(fullGroup.id),
                        });
                    }
                }
            }
            initializedRef.current = true;
        }
    }, [eventGroups, eventGroupsLoading, selectedEventGroup, location.search, navigate]);

    // Sync currentPage and searchQuery from URL when location changes (e.g. user navigates Back from Event details)
    useEffect(() => {
        setCurrentPage(getPageFromSearch(location.search));
        const params = new URLSearchParams(location.search);
        const searchFromUrl = params.get(SEARCH_PARAM);
        setSearchQuery(searchFromUrl !== null ? searchFromUrl : null);
    }, [location.search]);

    // When logged out, keep URL-driven state (page/group/search) so Back behavior
    // matches the logged-in experience. Only clear child selection.
    useEffect(() => {
        if (!user) {
            setSelectedChild(null);
        }
    }, [user, setSelectedChild]);

    // Reset the current page to 1 when the user types a non-empty search query.
    // Also update the URL so the PAGE_PARAM doesn't immediately push us back to the old page.
    useEffect(() => {
        const trimmed = debouncedSearchQuery?.trim() ?? '';
        if (!trimmed || trimmed.length === 0) return;

        const pageFromUrl = getPageFromSearch(location.search);
        if (pageFromUrl === 1 && currentPage === 1) return;

        // Move to page 1 in state
        setCurrentPage(1);

        // And normalize the URL by removing PAGE_PARAM so the URL-sync effect
        // doesn't immediately override currentPage back to the previous page.
        const params = new URLSearchParams(location.search);
        if (params.has(PAGE_PARAM)) {
            params.delete(PAGE_PARAM);
            const newSearch = params.toString();
            navigate(newSearch ? `/events?${newSearch}` : '/events', { replace: true });
        }
    }, [debouncedSearchQuery, location.search, currentPage, navigate]);

    // Persist search in URL when the user actively types, so Back from Event Details restores the search.
    // Do NOT override a search that comes from the URL itself until the user has interacted with the search box.
    useEffect(() => {
        if (location.pathname !== '/events') return;
        if (!hasUserTypedSearchRef.current) return;

        const params = new URLSearchParams(location.search);
        const trimmed = debouncedSearchQuery?.trim() ?? '';
        const desiredSearch = trimmed.length > 0 ? trimmed : null;
        const currentSearch = params.get(SEARCH_PARAM);
        if ((desiredSearch || '') === (currentSearch || '')) return;

        if (desiredSearch) {
            params.set(SEARCH_PARAM, desiredSearch);
        } else if (currentSearch) {
            params.delete(SEARCH_PARAM);
        } else {
            return;
        }

        const newSearch = params.toString();
        navigate(newSearch ? `/events?${newSearch}` : '/events', { replace: true });
    }, [debouncedSearchQuery, location.pathname, location.search]);

    const handleSearchChange = (newQuery: string) => {
        hasUserTypedSearchRef.current = true;
        setSearchQuery(newQuery);
    };

    const handleChildTabChange = (child: Child) => {
        setSelectedChild(child);
        // Clear the group id from the url
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete('group_id');
        navigate(`?${searchParams.toString()}`, { replace: true });
    };

    const [cartDrawerOpen, setCartDrawerOpen] = useState(false);

    const handleEventClick = (event: any) => {
        if (event && event.uuid) {
            // Store return path including page, group_id, and search so Back from Event restores the same view
            const searchParams = new URLSearchParams(window.location.search);
            if (currentPage > 1) {
                searchParams.set(PAGE_PARAM, String(currentPage));
            }
            if (selectedEventGroup && selectedEventGroup.id !== ALL_EVENTS_GROUP.id) {
                if (!searchParams.get('group_id')) {
                    searchParams.set('group_id', selectedEventGroup.id);
                }
            }
            if (debouncedSearchQuery && debouncedSearchQuery.trim().length >= 3) {
                searchParams.set(SEARCH_PARAM, debouncedSearchQuery.trim());
            }
            const returnPath = '/events' + (searchParams.toString() ? `?${searchParams.toString()}` : '');
            storeReturnToForEvents(returnPath);
            navigate(`/event/${event.uuid}`, { state: { event } });
        } else {
            console.error('Event click failed: event uuid/id is missing', event);
        }
    };

    const { loading: eventFetchingLoading, filteredEvents, totalPages, emptyDueToAge } = useEventFetching({
        selectedEventGroupId: selectedEventGroup?.id != null ? String(selectedEventGroup.id) : null,
        selectedChild,
        currentPage,
        searchQuery: debouncedSearchQuery && debouncedSearchQuery.trim().length >= 3 ? debouncedSearchQuery : null,
        user
    });

    const handleGroupChange = (group: any) => {
        setSelectedEventGroup(group);
        // Update URL with new group ID; reset page to 1 when group changes
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete(PAGE_PARAM);
        if (group.id === ALL_EVENTS_GROUP.id) {
            searchParams.delete('group_id');
        } else {
            searchParams.set('group_id', group.id);
        }
        navigate(`?${searchParams.toString()}`, { replace: true });
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
        const searchParams = new URLSearchParams(window.location.search);
        if (page === 1) {
            searchParams.delete(PAGE_PARAM);
        } else {
            searchParams.set(PAGE_PARAM, String(page));
        }
        const newSearch = searchParams.toString();
        navigate(newSearch ? `/events?${newSearch}` : '/events', { replace: true });
    };

    const isContentLoading = eventGroupsLoading || eventFetchingLoading;
    const hasEventsToShow = selectedEventGroup && filteredEvents.length > 0;
    const showStandaloneLoader = isContentLoading && !hasEventsToShow;

    return (
        <>
            <div className="event-page p-5 mb-20">
                <Box
                    className="events-main-page pt-5 pt-xs-0"
                    sx={{ width: '100%', maxWidth: EVENTS_PAGE_MAX_WIDTH, mx: 'auto' }}
                >
                    <CartDrawer open={cartDrawerOpen} onClose={() => setCartDrawerOpen(false)} />

                    <Box className="hide-md pb-0" sx={{ mt: 1.5 }}>
                        {groupsLoaded ? (
                            <SelectSidebar
                                eventGroups={filteredEventGroups}
                                setSelectedEventGroup={handleGroupChange}
                                selectedEventGroup={selectedEventGroup}
                            />
                        ) : (
                            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            gap: 2.5,
                            mt: 3,
                            width: '100%',
                        }}
                    >
                        <Box
                            className="hide-mobile"
                            sx={{
                                width: EVENTS_SIDEBAR_WIDTH,
                                flexShrink: 0,
                                height: 'calc(100vh - 140px)',
                                minHeight: '360px',
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {groupsLoaded ? (
                                <Sidebar
                                    eventGroups={eventGroups}
                                    setSelectedEventGroup={handleGroupChange}
                                    selectedEventGroup={selectedEventGroup}
                                    onChildChange={handleChildTabChange}
                                />
                            ) : (
                                <>
                                    <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 1 }} />
                                    <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 1 }} />
                                </>
                            )}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                            <Box className="pb-5">
                                <SearchBar value={searchQuery ?? ''} onSearchChange={handleSearchChange} />
                            </Box>
                            {showStandaloneLoader ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        mt: 10,
                                        minHeight: '50vh',
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            ) : hasEventsToShow ? (
                                <Box sx={{ position: 'relative' }}>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: `repeat(auto-fit, ${EVENT_CARD_WIDTH}px)`,
                                            justifyContent: 'center',
                                            gap: `${CARD_GRID_GAP}px`,
                                            width: '100%',
                                            opacity: eventFetchingLoading ? 0.45 : 1,
                                            pointerEvents: eventFetchingLoading ? 'none' : 'auto',
                                            transition: 'opacity 0.2s ease',
                                        }}
                                    >
                                        {filteredEvents.map((event: { id: Key | null | undefined; uuid?: string }) => (
                                            <EventsCard
                                                key={event.id}
                                                event={event}
                                                onRegisterClick={() => handleEventClick(event)}
                                                selectedChildId={selectedChild?.id}
                                            />
                                        ))}
                                    </Box>
                                    {eventFetchingLoading && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                zIndex: 1,
                                            }}
                                        >
                                            <CircularProgress />
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <p className="p-info bigger mt-10">
                                    {getEventsEmptyMessage({
                                        selectedChild,
                                        selectedEventGroup,
                                        hasActiveSearch: Boolean(
                                            debouncedSearchQuery?.trim() &&
                                            debouncedSearchQuery.trim().length >= 3
                                        ),
                                        emptyDueToAge,
                                    })}
                                </p>
                            )}
                            {!isContentLoading && filteredEvents.length > 0 && (
                                <div className="pagination-events">
                                    <Pagination
                                        count={totalPages}
                                        page={currentPage}
                                        onChange={handlePageChange}
                                        color="primary"
                                    />
                                </div>
                            )}
                        </Box>
                    </Box>
                </Box>
            </div>
        </>
    );
};

export default MainLayout;
